import React, { useState, useEffect } from 'react';
import { analyticsAPI, resultsAPI, organizationsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import {
  Sparkles,
  Send,
  BarChart3,
  TrendingUp,
  Target,
  Loader2,
  RefreshCw,
  Zap,
  Clock,
  Database,
  DollarSign,
  LineChartIcon,
  PieChartIcon,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';

const SUGGESTED_PROMPTS = [
  "Analyze my scoring trends over the past tournaments",
  "What are my strengths and weaknesses based on results?",
  "Recommend tournaments I should attend next",
  "How can I improve my performance?",
  "Compare my scores across different event types",
  "What's my consistency like in competitions?"
];

const CHART_COLORS = {
  primary: '#1e40af',
  secondary: '#3b82f6',
  tertiary: '#60a5fa',
  success: '#10b981',
  warning: '#f59e0b',
  danger: '#ef4444'
};

const ORG_COLORS = {
  USAA: '#1e40af',
  USAT: '#059669',
  NFAA: '#dc2626',
  TSAA: '#7c3aed',
  TFAA: '#ea580c',
  ASA: '#0891b2',
  IBO: '#be185d',
  OTHER: '#6b7280'
};

export default function Analytics() {
  const { isAdmin } = useAuth();
  const [query, setQuery] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [orgsCount, setOrgsCount] = useState(0);
  const [usageStats, setUsageStats] = useState(null);
  const [activeTab, setActiveTab] = useState('charts');

  useEffect(() => {
    fetchData();
    fetchUsage();
  }, []);

  const fetchData = async () => {
    try {
      const [resultsRes, orgsRes] = await Promise.all([
        resultsAPI.getAll(),
        organizationsAPI.getAll()
      ]);
      setResults(resultsRes.data);
      setOrgsCount(orgsRes.data.length);
    } catch (error) {
      console.error('Failed to fetch stats');
    }
  };

  const fetchUsage = async () => {
    try {
      const response = await analyticsAPI.getUsage();
      setUsageStats(response.data);
    } catch (error) {
      console.error('Failed to fetch usage stats');
    }
  };

  const handleAnalyze = async () => {
    if (!query.trim()) {
      toast.error('Please enter a question');
      return;
    }

    if (usageStats?.cooldown_seconds > 0) {
      toast.error(`Please wait ${usageStats.cooldown_seconds} seconds before your next request`);
      return;
    }

    setLoading(true);
    setAnalysis(null);

    try {
      const response = await analyticsAPI.analyze(query);
      setAnalysis(response.data);
      fetchUsage();
    } catch (error) {
      const message = error.response?.data?.detail || 'Analysis failed. Please try again.';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handlePromptClick = (prompt) => {
    setQuery(prompt);
  };

  // Process data for charts
  const processChartData = () => {
    if (!results.length) return { timeline: [], byOrg: [], byType: [], percentage: [] };

    // Sort by date
    const sortedResults = [...results].sort((a, b) => 
      new Date(a.event_date) - new Date(b.event_date)
    );

    // Timeline data (scoring trend)
    const timeline = sortedResults.map((r, idx) => ({
      name: r.tournament_name.length > 15 ? r.tournament_name.slice(0, 15) + '...' : r.tournament_name,
      date: new Date(r.event_date).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }),
      score: r.score,
      maxScore: r.max_score || r.score,
      percentage: r.max_score ? Math.round((r.score / r.max_score) * 100) : null,
      placement: r.placement,
      org: r.org
    }));

    // Scores by organization
    const orgScores = {};
    results.forEach(r => {
      if (!orgScores[r.org]) {
        orgScores[r.org] = { totalScore: 0, count: 0, totalMax: 0 };
      }
      orgScores[r.org].totalScore += r.score;
      orgScores[r.org].count += 1;
      if (r.max_score) {
        orgScores[r.org].totalMax += r.max_score;
      }
    });

    const byOrg = Object.entries(orgScores).map(([org, data]) => ({
      name: org,
      avgScore: Math.round(data.totalScore / data.count),
      events: data.count,
      fill: ORG_COLORS[org] || ORG_COLORS.OTHER
    }));

    // Scores by event type
    const typeScores = {};
    results.forEach(r => {
      const type = r.event_type || 'Unknown';
      if (!typeScores[type]) {
        typeScores[type] = { totalScore: 0, count: 0, totalMax: 0, placements: [] };
      }
      typeScores[type].totalScore += r.score;
      typeScores[type].count += 1;
      if (r.max_score) typeScores[type].totalMax += r.max_score;
      if (r.placement) typeScores[type].placements.push(r.placement);
    });

    const byType = Object.entries(typeScores).map(([type, data]) => ({
      name: type,
      avgScore: Math.round(data.totalScore / data.count),
      events: data.count,
      avgPlacement: data.placements.length > 0 
        ? Math.round(data.placements.reduce((a, b) => a + b, 0) / data.placements.length)
        : null
    }));

    // Percentage chart (only for results with max_score)
    const percentage = sortedResults
      .filter(r => r.max_score)
      .map(r => ({
        name: r.tournament_name.length > 12 ? r.tournament_name.slice(0, 12) + '...' : r.tournament_name,
        date: new Date(r.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        percentage: Math.round((r.score / r.max_score) * 100),
        org: r.org
      }));

    return { timeline, byOrg, byType, percentage };
  };

  const chartData = processChartData();
  const resultsCount = results.length;

  // Calculate stats
  const avgScore = resultsCount > 0
    ? Math.round(results.reduce((sum, r) => sum + r.score, 0) / resultsCount)
    : 0;
  
  const avgPercentage = results.filter(r => r.max_score).length > 0
    ? Math.round(
        results.filter(r => r.max_score)
          .reduce((sum, r) => sum + (r.score / r.max_score * 100), 0) / 
        results.filter(r => r.max_score).length
      )
    : null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="analytics-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">AI Analytics</h1>
          <p className="text-slate-600 mt-1">
            Visualize trends and get AI-powered insights on your archery performance
          </p>
        </div>
      </div>

      {/* Usage Stats Bar */}
      {usageStats && (
        <Card className="card-base border-amber-200 bg-amber-50/50">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <Zap className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-medium text-slate-700">
                    Today: {usageStats.daily?.used || 0}/{usageStats.daily?.limit || 5}
                  </span>
                  <Progress 
                    value={(usageStats.daily?.used / usageStats.daily?.limit) * 100} 
                    className="w-20 h-2"
                  />
                </div>
                {/* Monthly count and Est. Cost - Admin only */}
                {isAdmin && (
                  <>
                    <div className="flex items-center gap-2">
                      <Database className="w-4 h-4 text-blue-600" />
                      <span className="text-sm font-medium text-slate-700">
                        Monthly: {usageStats.monthly?.used || 0}/{usageStats.monthly?.limit || 50}
                      </span>
                    </div>
                    {usageStats.stats && (
                      <div className="flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-slate-700">
                          Est. Cost: {usageStats.stats.estimated_cost}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </div>
              {usageStats.cooldown_seconds > 0 && (
                <Badge variant="outline" className="bg-white text-amber-700 border-amber-300">
                  <Clock className="w-3 h-3 mr-1" />
                  Wait {usageStats.cooldown_seconds}s
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Results Recorded</p>
                <p className="text-2xl font-bold text-slate-900">{resultsCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Avg Score</p>
                <p className="text-2xl font-bold text-slate-900">{avgScore}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-slate-500 font-medium">Avg Accuracy</p>
                <p className="text-2xl font-bold text-slate-900">
                  {avgPercentage ? `${avgPercentage}%` : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-base bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary/20 rounded-xl flex items-center justify-center">
                <Sparkles className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-primary/70 font-medium">Powered by</p>
                <p className="text-lg font-bold text-primary">GPT-4o</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="charts" className="flex items-center gap-2" data-testid="charts-tab">
            <LineChartIcon className="w-4 h-4" />
            Scoring Trends
          </TabsTrigger>
          <TabsTrigger value="ai" className="flex items-center gap-2" data-testid="ai-tab">
            <Sparkles className="w-4 h-4" />
            AI Insights
          </TabsTrigger>
        </TabsList>

        {/* Charts Tab */}
        <TabsContent value="charts" className="space-y-6 mt-6">
          {resultsCount > 0 ? (
            <>
              {/* Score Timeline Chart */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-primary" />
                    Score Timeline
                  </CardTitle>
                  <CardDescription>
                    Your scores over time across all tournaments
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[300px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData.timeline}>
                        <defs>
                          <linearGradient id="scoreGradient" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={CHART_COLORS.primary} stopOpacity={0.3}/>
                            <stop offset="95%" stopColor={CHART_COLORS.primary} stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis 
                          dataKey="date" 
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickLine={false}
                        />
                        <YAxis 
                          tick={{ fontSize: 12, fill: '#64748b' }}
                          tickLine={false}
                          axisLine={false}
                        />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px',
                            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                          }}
                          formatter={(value, name) => [value, name === 'score' ? 'Score' : name]}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="score" 
                          stroke={CHART_COLORS.primary}
                          strokeWidth={2}
                          fill="url(#scoreGradient)"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Percentage Trend */}
                {chartData.percentage.length > 0 && (
                  <Card className="card-base">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-emerald-600" />
                        Accuracy Trend
                      </CardTitle>
                      <CardDescription>
                        Score as percentage of max possible
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart data={chartData.percentage}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                            <XAxis 
                              dataKey="date" 
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                            />
                            <YAxis 
                              domain={[0, 100]}
                              tick={{ fontSize: 11, fill: '#64748b' }}
                              tickLine={false}
                              axisLine={false}
                              tickFormatter={(v) => `${v}%`}
                            />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: 'white',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px'
                              }}
                              formatter={(value) => [`${value}%`, 'Accuracy']}
                            />
                            <Line 
                              type="monotone" 
                              dataKey="percentage" 
                              stroke={CHART_COLORS.success}
                              strokeWidth={2}
                              dot={{ fill: CHART_COLORS.success, strokeWidth: 2 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* By Organization */}
                <Card className="card-base">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChartIcon className="w-5 h-5 text-blue-600" />
                      Performance by Organization
                    </CardTitle>
                    <CardDescription>
                      Average scores across different governing bodies
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[250px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={chartData.byOrg} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
                          <XAxis type="number" tick={{ fontSize: 11, fill: '#64748b' }} />
                          <YAxis 
                            type="category" 
                            dataKey="name" 
                            tick={{ fontSize: 11, fill: '#64748b' }}
                            width={50}
                          />
                          <Tooltip
                            contentStyle={{
                              backgroundColor: 'white',
                              border: '1px solid #e2e8f0',
                              borderRadius: '8px'
                            }}
                            formatter={(value, name, props) => [
                              name === 'avgScore' ? `${value} avg` : `${value} events`,
                              name === 'avgScore' ? 'Score' : 'Events'
                            ]}
                          />
                          <Bar dataKey="avgScore" radius={[0, 4, 4, 0]}>
                            {chartData.byOrg.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.fill} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* By Event Type */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-purple-600" />
                    Performance by Event Type
                  </CardTitle>
                  <CardDescription>
                    Compare your performance across different event types
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="h-[250px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData.byType}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="name" tick={{ fontSize: 12, fill: '#64748b' }} />
                        <YAxis tick={{ fontSize: 12, fill: '#64748b' }} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #e2e8f0',
                            borderRadius: '8px'
                          }}
                        />
                        <Legend />
                        <Bar dataKey="avgScore" name="Avg Score" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                        <Bar dataKey="events" name="Events" fill={CHART_COLORS.secondary} radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="card-base">
              <CardContent className="py-16 text-center">
                <LineChartIcon className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No Data to Display</h3>
                <p className="text-slate-500 max-w-md mx-auto">
                  Add tournament results to see your scoring trends and performance charts.
                  Go to the Results page to add your first result.
                </p>
                <Button 
                  variant="outline" 
                  className="mt-4"
                  onClick={() => window.location.href = '/results'}
                >
                  Go to Results
                </Button>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="ai" className="mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Query Input */}
            <div className="lg:col-span-1 space-y-4">
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-lg">Ask AI</CardTitle>
                  <CardDescription>
                    Get insights about your archery performance
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <Textarea
                    placeholder="Ask a question about your performance..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    rows={4}
                    className="resize-none"
                    data-testid="analytics-query-input"
                  />
                  <Button
                    className="w-full btn-primary"
                    onClick={handleAnalyze}
                    disabled={loading || !query.trim()}
                    data-testid="analyze-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Analyze
                      </>
                    )}
                  </Button>
                </CardContent>
              </Card>

              {/* Suggested Prompts */}
              <Card className="card-base">
                <CardHeader>
                  <CardTitle className="text-sm">Suggested Questions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {SUGGESTED_PROMPTS.map((prompt, idx) => (
                    <button
                      key={idx}
                      onClick={() => handlePromptClick(prompt)}
                      className="w-full text-left p-3 text-sm text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-lg transition-colors"
                      data-testid={`suggested-prompt-${idx}`}
                    >
                      {prompt}
                    </button>
                  ))}
                </CardContent>
              </Card>
            </div>

            {/* Analysis Result */}
            <div className="lg:col-span-2">
              <Card className="card-base h-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-primary" />
                      AI Analysis
                    </CardTitle>
                    {analysis && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setAnalysis(null)}
                        data-testid="clear-analysis-btn"
                      >
                        <RefreshCw className="w-4 h-4 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="flex flex-col items-center justify-center py-12">
                      <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
                      <p className="text-slate-600 font-medium">Analyzing your data...</p>
                      <p className="text-sm text-slate-500 mt-1">This may take a few seconds</p>
                    </div>
                  ) : analysis ? (
                    <div className="space-y-4">
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <p className="text-sm text-slate-500 font-medium mb-1">Your Question:</p>
                        <p className="text-slate-900">{analysis.query}</p>
                      </div>
                      <ScrollArea className="h-[400px]">
                        <div className="prose prose-slate max-w-none">
                          <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                            {analysis.analysis}
                          </div>
                        </div>
                      </ScrollArea>
                      
                      {/* Token Usage Info - Cost details only for admin */}
                      <div className="pt-4 border-t border-slate-200 space-y-3">
                        {analysis.token_usage && (
                          <div className="flex flex-wrap items-center gap-3">
                            {analysis.cached ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                <Database className="w-3 h-3 mr-1" />
                                Cached Result
                              </Badge>
                            ) : (
                              <>
                                {isAdmin && (
                                  <>
                                    <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                      <Zap className="w-3 h-3 mr-1" />
                                      {analysis.token_usage.total_tokens?.toLocaleString() || 0} tokens
                                    </Badge>
                                    {analysis.token_usage.cost_estimate > 0 && (
                                      <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                                        <DollarSign className="w-3 h-3 mr-1" />
                                        ~${analysis.token_usage.cost_estimate.toFixed(4)}
                                      </Badge>
                                    )}
                                  </>
                                )}
                              </>
                            )}
                          </div>
                        )}
                        
                        {isAdmin && analysis.limits && (
                          <div className="text-xs text-slate-500">
                            Daily: {analysis.limits.daily_used}/{analysis.limits.daily_limit} • 
                            Monthly: {analysis.limits.monthly_used}/{analysis.limits.monthly_limit}
                          </div>
                        )}
                        
                        <p className="text-xs text-slate-400">
                          Analysis generated at {new Date(analysis.timestamp).toLocaleString()}
                          {isAdmin && analysis.token_usage?.model && ` • Model: ${analysis.token_usage.model}`}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                        <TrendingUp className="w-10 h-10 text-slate-400" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">Ready to Analyze</h3>
                      <p className="text-slate-500 max-w-sm">
                        Ask a question about your tournament results, performance trends, 
                        or get recommendations for upcoming events.
                      </p>
                      {resultsCount === 0 && (
                        <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-sm text-amber-700">
                            <strong>Tip:</strong> Add some tournament results first to get more meaningful insights.
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
